import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MAINTENANCE_CONFIG, isFeatureAllowed } from '../config/maintenance';
import { shouldShowServicesInNav } from '../config/services';
import { shouldShowBookingIn, getBookingButtonText } from '../config/booking';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Handle potential Auth context issues
  let currentUser = null;
  let isAdmin = false;
  let isSpecialUser = false;
  let logout = () => {};
  
  try {
    const auth = useAuth();
    currentUser = auth.currentUser;
    isAdmin = auth.isAdmin;
    isSpecialUser = auth.isSpecialUser;
    logout = auth.logout;
  } catch (error) {
    console.log('Auth context not available:', error);
  }
  
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleNavClick = (href, isRoute = false) => {
    if (isRoute) {
      // Direct route navigation - scroll to top after navigation
      navigate(href);
      // Scroll to top immediately for route changes
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 0);
    } else {
      // Hash navigation - need to go to home page first if not already there
      if (location.pathname !== '/') {
        navigate('/');
        // Wait a bit for the navigation to complete, then scroll
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll to section
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setIsMenuOpen(false);
  };

  const allowedNavItems = [
    { name: 'Home', href: '#home', feature: 'home' },
    { name: 'About', href: '#about', feature: 'about' },
    { name: 'Doctor', href: '#doctor', feature: 'doctor' },
    ...(shouldShowServicesInNav() ? [{ name: 'Services', href: '#services', feature: 'services' }] : []),
    { name: 'Blog', href: '/blog', isRoute: true, feature: 'blog' },
  ].filter(item => isFeatureAllowed(item.feature));

  return (
    <header ref={headerRef} className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:justify-start md:space-x-10">
          {/* Logo */}
          <div className="flex justify-start lg:w-0 lg:flex-1">
            <Link to="/" className="flex items-center">
              <div className="w-10 h-10 bg-gradient-green rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">🕉</span>
              </div>
              <span className="ml-3 text-2xl font-bold text-green-800">
                Sravani <span className="text-brown-600">Ayurdham</span>
              </span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
                setShowUserDropdown(false); // Close user dropdown when toggling mobile menu
              }}
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                />
              </svg>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {allowedNavItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavClick(item.href, item.isRoute)}
                className="text-gray-700 hover:text-green-600 px-3 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer"
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Auth & Appointment Section */}
          <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0 space-x-4">
            {isFeatureAllowed('login') && currentUser ? (
              <div className="relative">
                {/* User Profile Button */}
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-full bg-green-50 hover:bg-green-100 transition-colors duration-200 border border-green-200"
                >
                  {/* User Avatar */}
                  <div className="w-8 h-8 bg-gradient-green rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {(currentUser.displayName || currentUser.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  {/* User Name */}
                  <span className="text-sm font-medium text-gray-700 max-w-32 truncate">
                    {currentUser.displayName || currentUser.email.split('@')[0]}
                  </span>
                  {/* Dropdown Arrow */}
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${
                      showUserDropdown ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {showUserDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-fadeIn">
                    <div className="absolute -top-2 right-6 w-4 h-4 bg-white border-l border-t border-gray-100 transform rotate-45"></div>
                    
                    {/* User Info Section */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-green rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {(currentUser.displayName || currentUser.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {currentUser.displayName || 'Welcome'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {currentUser.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      {isSpecialUser && (
                        <>
                          <Link
                            to="/baby-tracker"
                            onClick={() => setShowUserDropdown(false)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            👶 Baby Tracker
                          </Link>
                          <Link
                            to="/contraction-tracker"
                            onClick={() => setShowUserDropdown(false)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600 transition-colors duration-200"
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            🤰 Contraction Tracker
                          </Link>
                        </>
                      )}
                      
                      {isAdmin && isFeatureAllowed('admin') && (
                        <Link
                          to="/admin/articles"
                          onClick={() => setShowUserDropdown(false)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Admin Panel
                        </Link>
                      )}

                      <div className="border-t border-gray-100 my-2"></div>
                      
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowUserDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : isFeatureAllowed('login') && !currentUser ? (
              <Link
                to="/login"
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Login
              </Link>
            ) : null}
            
            {/* Maintenance Notice for Disabled Features */}
            {MAINTENANCE_CONFIG.isMaintenanceMode && !isFeatureAllowed('contact') && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-orange-50 rounded-full border border-orange-200">
                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.586V5L8 4z" />
                </svg>
                <span className="text-sm font-medium text-orange-700">Under Maintenance</span>
              </div>
            )}

            {isFeatureAllowed('contact') && shouldShowBookingIn('Header') && (
              <button
                onClick={() => handleNavClick('#contact')}
                className="bg-gradient-green text-white px-6 py-2 rounded-full font-medium hover:bg-green-700 transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                {getBookingButtonText('primary')}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              {allowedNavItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href, item.isRoute)}
                  className="text-gray-700 hover:text-green-600 block px-3 py-2 text-base font-medium w-full text-left"
                >
                  {item.name}
                </button>
              ))}
              
              {/* Disabled Features Notice */}
              {MAINTENANCE_CONFIG.isMaintenanceMode && (
                <div className="px-3 py-2 bg-orange-50 rounded-lg border border-orange-200 mx-1">
                  <p className="text-sm font-medium text-orange-700 mb-2">Under Maintenance:</p>
                  <div className="text-xs text-orange-600 space-y-1">
                    {!isFeatureAllowed('services') && <p>• Services temporarily unavailable</p>}
                    {!isFeatureAllowed('blog') && <p>• Blog access restricted</p>}
                    {!isFeatureAllowed('login') && <p>• Login temporarily disabled</p>}
                    {!isFeatureAllowed('contact') && <p>• Appointments via emergency contact</p>}
                  </div>
                </div>
              )}
              
              {/* Mobile Auth Section */}
              <div className="border-t pt-4 mt-4">
                {isFeatureAllowed('login') && currentUser ? (
                  <div className="space-y-3">
                    {/* Mobile User Profile */}
                    <div className="flex items-center space-x-3 px-3 py-3 bg-green-50 rounded-lg">
                      <div className="w-10 h-10 bg-gradient-green rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {(currentUser.displayName || currentUser.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {currentUser.displayName || 'Welcome'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentUser.email}
                        </p>
                      </div>
                    </div>

                    {/* Mobile Menu Items */}
                    <div className="space-y-1">
                      {isSpecialUser && (
                        <>
                          <Link
                            to="/baby-tracker"
                            className="flex items-center w-full px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            👶 Baby Tracker
                          </Link>
                          <Link
                            to="/contraction-tracker"
                            className="flex items-center w-full px-3 py-2 text-gray-700 hover:text-pink-600 hover:bg-pink-50 rounded-md transition-colors duration-200"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            🤰 Contraction Tracker
                          </Link>
                        </>
                      )}
                      
                      {isAdmin && isFeatureAllowed('admin') && (
                        <Link
                          to="/admin/articles"
                          className="flex items-center w-full px-3 py-2 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Admin Panel
                        </Link>
                      )}

                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                ) : isFeatureAllowed('login') && !currentUser ? (
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-green-600 hover:text-green-800 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                ) : MAINTENANCE_CONFIG.isMaintenanceMode && !isFeatureAllowed('login') ? (
                  <div className="px-3 py-2 text-sm text-orange-600 bg-orange-50 rounded-md">
                    Login temporarily unavailable
                  </div>
                ) : null}
              </div>

              {isFeatureAllowed('contact') && shouldShowBookingIn('Header') ? (
                <button
                  onClick={() => handleNavClick('#contact')}
                  className="bg-gradient-green text-white block px-3 py-2 rounded-md text-base font-medium mt-4 w-full text-left"
                >
                  {getBookingButtonText('primary')}
                </button>
              ) : (
                <div className="mt-4 px-3">
                  <div className="w-full bg-orange-100 text-orange-700 px-3 py-2 rounded-md font-medium text-center text-sm">
                    Emergency Contact: {MAINTENANCE_CONFIG.emergencyContact}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
